export interface DashboardV4Data {
  // Header data
  user: {
    name: string;
    avatar?: string;
    lastSessionTime?: string;
  };

  // Activity feed data
  whatsChanged: ChangeItem[];
  whatNeedsAttention: AttentionItem[];

  // Key metrics
  totalOpenSignals: number;
  totalOpenCases: number;
  averageConductScore: number;
  averageStrainScore?: number;
  averageHarmScore?: number;

  // Chart data
  signalsAndCasesOverTime: TimeSeriesData[];
  complaintDetection: ComplaintDetectionData[];
  channelBreakdown: ChannelData[];
  vulnerabilityDetection: VulnerabilityData;
  frictionHotspots?: FrictionData;
  openCasesByAge: CaseAgeData[];
  resolutionRate?: ResolutionData;
  topRootCauses: RootCauseData[];
}

export interface ChangeItem {
  id: string;
  count: number;
  title: string;
  description: string;
}

export interface AttentionItem {
  id: string;
  type: 'critical' | 'high' | 'medium';
  title: string;
  description: string;
  actionLabel: string;
  actionPath?: string;
  signalId: string;
  customerName?: string;
  severity?: number;
  // Specialized summaries from classification
  harmSummary?: string;
  strainSummary?: string;
  vulnerabilitySummary?: string;
  regulatoryAssessment?: string;
}

export interface TimeSeriesData {
  date: string;
  signals: number;
  cases: number;
}

export interface ComplaintDetectionData {
  date: string;
  dissatisfaction: number;  // Maps to 'No Complaint' in taxonomy-based classification
  potential: number;        // Maps to 'Potential Complaint' in taxonomy-based classification
  formal: number;           // Maps to 'Formal Complaint' in taxonomy-based classification
}

export interface ComplaintDetectionCategoryData {
  category: string;
  count: number;
  color: string;
  percentage: number;
}

export interface ChannelData {
  channel: string;
  count: number;
  color: string;
  icon?: string;
}

export interface VulnerabilityData {
  percentage: number;
  signalCount: number;
  weeklyChange: number;
}

export interface FrictionData {
  journeyStage: JourneyStageData[];
  productLine: ProductLineData[];
}

export interface JourneyStageData {
  stage: string;
  count: number;
  color: string;
}

export interface ProductLineData {
  product: string;
  count: number;
  color: string;
}

export interface CaseAgeData {
  ageRange: string;
  count: number;
  percentage: number;
  color: string;
}

export interface ResolutionData {
  percentage: number;
  averageTime: string;
  unit: string;
}

export interface RootCauseData {
  cause: string;
  count: number;
  percentage: number;
  color: string;
}

// Juno Dashboard Context for AI briefings
export interface JunoDashboardContext {
  summary: {
    totalSignals: number;
    criticalCount: number;
    vulnerableCount: number;
    slaRiskCount: number;
  };
  changes2weeks: {
    newCritical: number;
    newVulnerable: number;
    approachingSLA: number;
  };
  topConcerns: {
    signalId: string;
    summary: string;
    severity: number;
    isVulnerable: boolean;
  }[];
  timestamp: string;
}

// Extended dashboard data with Juno context
export interface DashboardV4DataWithContext extends DashboardV4Data {
  junoContext?: JunoDashboardContext;
}

// Chart styling constants
export const CHART_COLORS = {
  PRIMARY: '#4342FF',
  SECONDARY: '#60D5E0', 
  ACCENT: '#FF69B4',
  PURPLE: '#8B5CF6',
  RED: '#EF4444',
  GREEN: '#10B981',
  ORANGE: '#F59E0B',
  GRAY: '#6B7280',
  LIGHT_GRAY: '#F3F4F6'
} as const;

export const SEVERITY_COLORS = {
  HARM: '#EF4444',
  STRAIN: '#8B5CF6'
} as const;